import React, { useState, useEffect } from 'react';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ChildProgressCard from './components/ChildProgressCard';
import FamilyOverviewPanel from './components/FamilyOverviewPanel';
import CounselorCommunication from './components/CounselorCommunication';
import ProgressReportGenerator from './components/ProgressReportGenerator';
import AchievementHighlights from './components/AchievementHighlights';
import { useAuth } from '../../contexts/AuthContext';
import { getParentChildren, getChildProgress, calculateEngagementScore, calculateProgressPercentage } from '../../services/userService';

const ParentDashboardInterface = () => {
  const { user } = useAuth();
  const [selectedChild, setSelectedChild] = useState(null);
  const [viewMode, setViewMode] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [childrenProgress, setChildrenProgress] = useState({});

  useEffect(() => {
    const fetchChildrenData = async () => {
      if (!user?.id) return;
      
      setLoading(true);
      try {
        const { data: parentChildrenData } = await getParentChildren(user?.id);
        
        if (parentChildrenData && parentChildrenData?.length > 0) {
          const childrenList = parentChildrenData?.map(pc => ({
            id: pc?.child?.id,
            name: pc?.child?.full_name || 'Enfant',
            age: pc?.child?.age || 0,
            grade: pc?.child?.grade || 'Non spécifié',
            school: pc?.child?.school || 'Non spécifié',
            avatar: pc?.child?.full_name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase() || 'E',
            lastActive: pc?.child?.created_at
          }));
          
          setChildren(childrenList);
          
          if (childrenList?.length > 0 && !selectedChild) {
            setSelectedChild(childrenList?.[0]?.id);
          }
          
          const progressData = {};
          for (const child of childrenList) {
            const progress = await getChildProgress(child?.id);
            progressData[child.id] = {
              quizCompleted: progress?.quizResults?.length || 0,
              recommendationsReceived: progress?.progressReports?.length || 0,
              achievementsUnlocked: progress?.achievements?.length || 0,
              engagementScore: calculateEngagementScore(progress?.quizResults, progress?.achievements),
              progressPercentage: calculateProgressPercentage(progress?.quizResults, progress?.achievements),
              recentActivity: progress?.quizResults?.[0]?.quiz_title 
                ? `Quiz complété: ${progress?.quizResults?.[0]?.quiz_title}` 
                : 'Aucune activité récente'
            };
          }
          setChildrenProgress(progressData);
        }
      } catch (error) {
        console.error('Error fetching children data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChildrenData();
  }, [user?.id]);

  const enrichedChildren = children?.map(child => ({
    ...child,
    ...childrenProgress?.[child?.id]
  }));

  const currentChild = enrichedChildren?.find(c => c?.id === selectedChild);

  const handleScheduleSession = () => {
    console.log('Planification d\'une session familiale');
  };

  const handleGenerateReport = () => {
    console.log('Génération du rapport pour:', currentChild?.name);
  };

  const handleMessageCounselor = () => {
    console.log('Envoi d\'un message au conseiller');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <main className="pt-20 pb-8 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Chargement des données...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (children?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <main className="pt-20 pb-8 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-12">
              <Icon name="Users" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-foreground mb-2">Aucun enfant enregistré</h2>
              <p className="text-muted-foreground mb-6">Vous n'avez pas encore ajouté d'enfants à votre compte.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <main className="pt-20 pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                  Tableau de Bord Parental
                </h1>
                <p className="text-base text-muted-foreground">
                  Suivez le parcours d'orientation de vos enfants et communiquez avec leurs conseillers
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  iconName="Calendar"
                  onClick={handleScheduleSession}
                >
                  Planifier Session Familiale
                </Button>
                <Button
                  iconName="FileText"
                  onClick={handleGenerateReport}
                >
                  Générer Rapport
                </Button>
              </div>
            </div>

            {/* View Mode Tabs */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setViewMode('overview')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'overview' ? 'bg-primary text-primary-foreground' : 'bg-card text-card-foreground hover:bg-muted'
                }`}
              >
                <Icon name="LayoutDashboard" size={16} className="inline mr-2" />
                Vue d'Ensemble
              </button>
              <button
                onClick={() => setViewMode('progress')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'progress' ? 'bg-primary text-primary-foreground' : 'bg-card text-card-foreground hover:bg-muted'
                }`}
              >
                <Icon name="TrendingUp" size={16} className="inline mr-2" />
                Progression Détaillée
              </button>
              <button
                onClick={() => setViewMode('communication')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'communication' ? 'bg-primary text-primary-foreground' : 'bg-card text-card-foreground hover:bg-muted'
                }`}
              >
                <Icon name="MessageSquare" size={16} className="inline mr-2" />
                Communication
              </button>
            </div>
          </div>

          {/* Family Overview Panel */}
          {viewMode === 'overview' && (
            <div className="mb-8">
              <FamilyOverviewPanel children={enrichedChildren} />
            </div>
          )}

          {/* Child Selection */}
          <div className="mb-8">
            <div className="bg-card rounded-xl p-6 border border-border shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <Icon name="Users" size={24} className="text-primary" />
                <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                  Sélection de l'Enfant
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {enrichedChildren?.map((child) => (
                  <ChildProgressCard
                    key={child?.id}
                    child={child}
                    isSelected={child?.id === selectedChild}
                    onClick={() => setSelectedChild(child?.id)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          {viewMode === 'overview' && currentChild && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AchievementHighlights child={currentChild} />
              <ProgressReportGenerator child={currentChild} onGenerate={handleGenerateReport} />
            </div>
          )}

          {viewMode === 'progress' && currentChild && (
            <div className="space-y-6">
              <div className="bg-card rounded-xl p-6 border border-border shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <Icon name="BarChart" size={24} className="text-primary" />
                  <h2 className="text-xl font-semibold text-foreground">
                    Analyse de Progression - {currentChild?.name}
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="ClipboardCheck" size={20} className="text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">Quiz Complétés</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">{currentChild?.quizCompleted || 0}</p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Lightbulb" size={20} className="text-green-600" />
                      <span className="text-sm font-medium text-green-900">Recommandations</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600">{currentChild?.recommendationsReceived || 0}</p>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Award" size={20} className="text-purple-600" />
                      <span className="text-sm font-medium text-purple-900">Badges</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-600">{currentChild?.achievementsUnlocked || 0}</p>
                  </div>

                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="TrendingUp" size={20} className="text-amber-600" />
                      <span className="text-sm font-medium text-amber-900">Engagement</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-600">{currentChild?.engagementScore || 0}%</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Progression Globale</h3>
                  <div className="mb-2">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700">Parcours d'orientation</span>
                      <span className="font-semibold text-gray-900">{currentChild?.progressPercentage || 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${currentChild?.progressPercentage || 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <AchievementHighlights child={currentChild} detailed />
            </div>
          )}

          {viewMode === 'communication' && currentChild && (
            <CounselorCommunication
              child={currentChild}
              onMessageSend={handleMessageCounselor}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default ParentDashboardInterface;